import type {
  CreateModelRequest,
  CreateModelAliasRequest,
  CreateModelAliasResponse,
  CreateModelResponse,
  CreatePricingRequest,
  CreatePricingResponse,
  CreateProtocolRequest,
  CreateProtocolResponse,
  CreateUnitPricingRequest,
  CreateUnitPricingResponse,
  CurrentPricingResponse,
  DeleteModelResponse,
  DeleteModelAliasResponse,
  DeleteProtocolResponse,
  DeprecateModelRequest,
  DeprecateModelResponse,
  GetModelResponse,
  ListModelsResponse,
  ListPricingResponse,
  ListProtocolsResponse,
  ListUnitPricingResponse,
  ModelAliasRecord,
  ModelPricingRecord,
  ModelProtocol,
  ModelProtocolRecord,
  ModelRecord,
  ModelUnitPricingRecord,
  UndeprecateModelResponse,
  UpdateModelShelfRequest,
  UpdateModelShelfResponse,
  UpdateModelRequest,
  UpdateModelResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `ops-api /v1/ops/models/*`.
 *
 * Three logical resources share this module because they share the
 * `(vendor, model)` path prefix:
 *   - `models` CRUD
 *   - `models/:vendor/:model/pricing` (append-only by `effective_from`)
 *   - `models/:vendor/:model/protocols`
 *
 * Wire types come from `@nadoutong/contracts` — ops-api is a thin proxy
 * over platform-api `/internal/models/*`, so the shapes match across the
 * three layers.
 */

const BASE_PATH = '/v1/ops/models';

// ─── models CRUD ─────────────────────────────────────────────────────────

export async function listModels(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelRecord[]> {
  const response = await opsApiRequest<ListModelsResponse>(
    BASE_PATH,
    { signal },
    fetchImpl,
  );
  return response.models;
}

export async function getModel(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelRecord> {
  const response = await opsApiRequest<GetModelResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}`,
    { signal },
    fetchImpl,
  );
  return response.model;
}

export async function createModel(
  body: CreateModelRequest,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<CreateModelResponse>(
    BASE_PATH,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.model;
}

export async function updateModel(
  vendor: string,
  model: string,
  body: UpdateModelRequest,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<UpdateModelResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.model;
}

export async function deleteModel(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
): Promise<DeleteModelResponse> {
  return opsApiRequest<DeleteModelResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listModel(
  vendor: string,
  model: string,
  body: UpdateModelShelfRequest,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<UpdateModelShelfResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/list`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.model;
}

export async function delistModel(
  vendor: string,
  model: string,
  body: UpdateModelShelfRequest,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<UpdateModelShelfResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/delist`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.model;
}

export async function createModelAlias(
  vendor: string,
  model: string,
  body: CreateModelAliasRequest,
  fetchImpl: typeof fetch,
): Promise<ModelAliasRecord> {
  const response = await opsApiRequest<CreateModelAliasResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/aliases`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.alias;
}

export async function deleteModelAlias(
  vendor: string,
  model: string,
  alias: string,
  fetchImpl: typeof fetch,
): Promise<DeleteModelAliasResponse> {
  return opsApiRequest<DeleteModelAliasResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}` +
      `/aliases/${encodeURIComponent(alias)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

// ─── deprecation lifecycle ───────────────────────────────────────────────

export async function deprecateModel(
  vendor: string,
  model: string,
  body: DeprecateModelRequest,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<DeprecateModelResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/deprecate`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.model;
}

export async function undeprecateModel(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
): Promise<ModelRecord> {
  const response = await opsApiRequest<UndeprecateModelResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/undeprecate`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.model;
}

// ─── pricing ─────────────────────────────────────────────────────────────

export async function listPricing(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPricingRecord[]> {
  const response = await opsApiRequest<ListPricingResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/pricing`,
    { signal },
    fetchImpl,
  );
  return response.pricing;
}

export async function getCurrentPricing(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPricingRecord> {
  const response = await opsApiRequest<CurrentPricingResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/pricing/current`,
    { signal },
    fetchImpl,
  );
  return response.pricing;
}

export async function createPricing(
  vendor: string,
  model: string,
  body: CreatePricingRequest,
  fetchImpl: typeof fetch,
): Promise<ModelPricingRecord> {
  const response = await opsApiRequest<CreatePricingResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/pricing`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.pricing;
}

// ─── unit pricing (#1669) ────────────────────────────────────────────────

export async function listUnitPricing(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelUnitPricingRecord[]> {
  const response = await opsApiRequest<ListUnitPricingResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/unit-pricing`,
    { signal },
    fetchImpl,
  );
  return response.unit_pricing;
}

export async function createUnitPricing(
  vendor: string,
  model: string,
  body: CreateUnitPricingRequest,
  fetchImpl: typeof fetch,
): Promise<ModelUnitPricingRecord[]> {
  const response = await opsApiRequest<CreateUnitPricingResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/unit-pricing`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.unit_pricing;
}

// ─── protocols ───────────────────────────────────────────────────────────

export async function listProtocols(
  vendor: string,
  model: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelProtocolRecord[]> {
  const response = await opsApiRequest<ListProtocolsResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/protocols`,
    { signal },
    fetchImpl,
  );
  return response.protocols;
}

export async function addProtocol(
  vendor: string,
  model: string,
  body: CreateProtocolRequest,
  fetchImpl: typeof fetch,
): Promise<ModelProtocolRecord> {
  const response = await opsApiRequest<CreateProtocolResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}/protocols`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.protocol;
}

export async function deleteProtocol(
  vendor: string,
  model: string,
  protocol: ModelProtocol,
  fetchImpl: typeof fetch,
): Promise<DeleteProtocolResponse> {
  return opsApiRequest<DeleteProtocolResponse>(
    `${BASE_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}` +
      `/protocols/${encodeURIComponent(protocol)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}
